import React, { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { testimonials } from '../../data/testimonials';

export const TestimonialsCarousel = ({ className = '' }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center' },
    [Autoplay({ delay: 6000, stopOnInteraction: true, stopOnMouseEnter: true })]
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className={`relative ${className}`}>
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 text-primary/5">
        <Quote className="w-full h-full" />
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        className="absolute left-0 md:left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-card shadow-lg border border-border hover:bg-primary hover:text-primary-foreground transition-all duration-300"
        aria-label="Previous testimonial"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-0 md:right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-card shadow-lg border border-border hover:bg-primary hover:text-primary-foreground transition-all duration-300"
        aria-label="Next testimonial"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Carousel */}
      <div className="overflow-hidden px-12 md:px-20" ref={emblaRef}>
        <div className="flex">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="flex-shrink-0 w-full px-4"
            >
              <motion.div
                initial={{ opacity: 0.5, scale: 0.95 }}
                animate={{
                  opacity: selectedIndex === index ? 1 : 0.5,
                  scale: selectedIndex === index ? 1 : 0.95,
                }}
                transition={{ duration: 0.4 }}
                className="max-w-3xl mx-auto text-center py-8"
              >
                <blockquote className="space-y-6">
                  <h3 className="text-xl md:text-2xl font-semibold text-foreground leading-relaxed">
                    &ldquo;{testimonial.headline}&rdquo;
                  </h3>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {testimonial.body}
                  </p>
                </blockquote>
                <div className="mt-8">
                  <div className="flex items-center justify-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-foreground">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.title}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              selectedIndex === index
                ? 'bg-primary w-8'
                : 'bg-primary/20 hover:bg-primary/40'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
